import { TZDate } from '@date-fns/tz'
import {
    Insight,
    InsightQueryDTO,
    InsightQueryType,
    SurveyAnswerInsight,
} from '@hazadapt-git/public-core-base'
import React, { FC, useCallback, useMemo, useRef } from 'react'

import { QueryBuilderValues } from '../components'
import { NamespacedPageProps } from '../lib/entities'
import {
    saveQuery,
    exportInsights,
    persistLocalPickerChangesOnNewConfig,
    getSingleInsight,
    buildQueryDateRange,
    refreshSurveyInsight,
} from '../lib/utils/insights'
import { useAppSelector } from '../lib/store'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { SurveyAnswerInsightTemplate } from '../components/templates/SurveyAnswerInsightTemplate'
import { shallowEqual } from 'react-redux'
import { toISOStringWithTimezone } from '../lib/utils'
import { startOfDay } from 'date-fns'
import { v4 } from 'uuid'
import { selectPrepChecks } from '../lib/slices/content'

interface SurveyAnswerInsightProps extends NamespacedPageProps {}

const pageId = v4()
export const SurveyAnswerInsights: FC<SurveyAnswerInsightProps> = ({
    user,
    organization,
    loading,
}) => {
    const [searchParams] = useSearchParams()
    const navigate = useNavigate()
    const location = useLocation()
    const [activeQuery, setActiveQuery] = React.useState<InsightQueryDTO>()
    const [retrievingInsights, setRetrievingInsights] =
        React.useState<boolean>(true)
    const [surveyInsight, setSurveyInsight] =
        React.useState<SurveyAnswerInsight>()
    const prepChecks = useAppSelector(selectPrepChecks, shallowEqual)

    const href = useMemo(() => {
        const pieces = location.pathname.split('/')
        return pieces[pieces.length - 1] + location.search
    }, [location.pathname, location.search])

    const hasMounted = useRef(false)

    const setSurvey = React.useCallback((insight: Insight) => {
        if (insight && insight.type === 'survey-answer') {
            setSurveyInsight(insight)
        }
        setRetrievingInsights(false)
    }, [])

    const refreshInsights = React.useCallback(
        async (
            existingQuery: InsightQueryDTO,
            reset = false,
            updatedParams = false,
            resetMap = false
        ) => {
            setRetrievingInsights(true)
            getSingleInsight({
                ...existingQuery,
                type: InsightQueryType.PREP_CHECK_QUESTION,
                start_date: updatedParams
                    ? existingQuery.start_date
                    : undefined,
                end_date: updatedParams ? existingQuery.end_date : undefined,
                reset,
                href,
                reset_map: resetMap,
            })
                .then((res) => {
                    const { insights, quick_stat_row_title, ...newQuery } = res
                    persistLocalPickerChangesOnNewConfig(
                        newQuery.grid_config,
                        existingQuery.grid_config
                    )
                    setActiveQuery(newQuery)
                    setSurvey(insights[0])
                })
                .catch(console.error)
                .finally(() => setRetrievingInsights(false))
        },
        [href, setSurvey]
    )

    const fetchData = React.useCallback(() => {
        setRetrievingInsights(true)
        getSingleInsight({
            type: InsightQueryType.PREP_CHECK_QUESTION,
            searchParams,
            href,
            reset_map: true,
        })
            .then((res) => {
                const { insights, ...query } = res
                setActiveQuery(query)
                setSurveyInsight(
                    insights[0].type === 'survey-answer'
                        ? insights[0]
                        : undefined
                )
            })
            .catch(console.error)
            .finally(() => setRetrievingInsights(false))
    }, [searchParams, href])

    React.useEffect(() => {
        if (!hasMounted.current) {
            fetchData()
            hasMounted.current = true
        }
    }, [fetchData])

    const onSaveQuery = useCallback(async () => {
        if (!activeQuery) return
        saveQuery({
            ...activeQuery,
            type: InsightQueryType.PREP_CHECK_QUESTION,
            insight_ids: [surveyInsight?.placement_id ?? ''],
        })
    }, [activeQuery, surveyInsight])

    const onRunQueryPress = React.useCallback(
        (values: QueryBuilderValues) =>
            new Promise<void>((resolve, reject) => {
                if (!activeQuery || !user) {
                    reject('No query to run, or no user')
                    return
                }
                const pickerTwoValues: string[] = Array.isArray(
                    values.pickerTwoValue
                )
                    ? values.pickerTwoValue
                    : [values.pickerTwoValue]
                const fullJurisdiction =
                    pickerTwoValues.length === 1 &&
                    pickerTwoValues[0] === 'jurisidction'
                const active_query: InsightQueryDTO = {
                    ...activeQuery,
                    prep_checks: values.pickerOneValue
                        ? [parseInt(values.pickerOneValue.toString())]
                        : [],
                    zips: fullJurisdiction
                        ? []
                        : pickerTwoValues.filter((v) =>
                              organization?.areas.zip.includes(v)
                          ),
                    counties: fullJurisdiction
                        ? []
                        : pickerTwoValues.filter((v) =>
                              organization?.areas.county.includes(v)
                          ),
                    states: fullJurisdiction
                        ? []
                        : pickerTwoValues.filter((v) =>
                              organization?.areas.state.includes(v)
                          ),
                    start_date: values.start_date ?? undefined,
                    end_date: values.end_date ?? undefined,
                }

                const newSearchParams = new URLSearchParams(location.search)
                if (values.pickerOneValue) {
                    newSearchParams.set(
                        'prep_checks',
                        values.pickerOneValue.toString()
                    )
                }
                if (values.pickerTwoValue) {
                    newSearchParams.set(
                        'zips',
                        values.pickerTwoValue.toString()
                    )
                }
                if (values.start_date) {
                    newSearchParams.set(
                        'start_date',
                        toISOStringWithTimezone(
                            startOfDay(new TZDate(values.start_date))
                        )
                    )
                }
                if (values.end_date) {
                    newSearchParams.set(
                        'end_date',
                        toISOStringWithTimezone(new TZDate(values.end_date))
                    )
                }
                refreshInsights(active_query, undefined, true)
                    .then(() => {
                        setActiveQuery(active_query)
                        navigate(
                            `${location.pathname}?${newSearchParams.toString()}`
                        )
                    })
                    .then(resolve)
                    .catch(reject)
            }),
        [
            activeQuery,
            user,
            location.search,
            location.pathname,
            refreshInsights,
            navigate,
            organization,
        ]
    )

    const updateSingleInsight = React.useCallback(
        async (placement_id: string, args: InsightQueryDTO) => {
            setRetrievingInsights(true)
            setActiveQuery((aq) =>
                aq
                    ? {
                          ...aq,
                          ...args,
                      }
                    : undefined
            )
            const newInsight = await refreshSurveyInsight(
                placement_id,
                InsightQueryType.PREP_CHECK_QUESTION,
                args
            )
            setSurveyInsight(newInsight)
            setRetrievingInsights(false)
        },
        []
    )

    const onReloadPress = React.useCallback(() => {
        if (!activeQuery) return
        refreshInsights(activeQuery, undefined, true)
    }, [activeQuery, refreshInsights])

    const onResetQueryPress = React.useCallback(() => {
        navigate('/insights/prep-checks')
    }, [navigate])

    // Resets query when user clicks link to page they are currently on
    React.useEffect(() => {
        const handleRefresh = () => onResetQueryPress()
        window.addEventListener('page-refresh', handleRefresh)

        return () => {
            window.removeEventListener('page-refresh', handleRefresh)
        }
    }, [onResetQueryPress])

    const prepCheckOptions = useMemo(
        () =>
            [...prepChecks]
                .sort((a, b) => (a.title < b.title ? -1 : 1))
                .map((pc) => ({ label: pc.title, value: pc.id })),
        [prepChecks]
    )

    const onNextQuestionPress = () => {
        if (surveyInsight?.next && activeQuery) {
            updateSingleInsight(surveyInsight.next.split('|')[0], activeQuery)
        }
    }

    const onPrevQuestionPress = () => {
        if (surveyInsight?.prev && activeQuery) {
            updateSingleInsight(surveyInsight.prev.split('|')[0], activeQuery)
        }
    }

    const onBack = () => {
        const newUrl = new URL('/insights/prep-checks', window.location.origin)
        if (activeQuery?.prep_checks)
            newUrl.searchParams.set(
                'prep_checks',
                activeQuery.prep_checks.join(',')
            )
        if (activeQuery?.zips)
            newUrl.searchParams.set('zips', activeQuery.zips.join(','))
        if (activeQuery?.counties)
            newUrl.searchParams.set('counties', activeQuery.counties.join(','))
        if (activeQuery?.states)
            newUrl.searchParams.set('states', activeQuery.states.join(','))
        if (activeQuery?.start_date)
            newUrl.searchParams.set(
                'start_date',
                new TZDate(activeQuery.start_date)
                    .withTimeZone('UTC')
                    .toISOString()
            )
        if (activeQuery?.end_date)
            newUrl.searchParams.set(
                'end_date',
                new TZDate(activeQuery.end_date)
                    .withTimeZone('UTC')
                    .toISOString()
            )
        navigate(`${newUrl.pathname}${newUrl.search}`)
    }

    return organization && user ? (
        <SurveyAnswerInsightTemplate
            organization={organization}
            queryItems={prepCheckOptions}
            selectedQueryItem={activeQuery?.prep_checks?.[0]}
            selectedAreas={[
                ...(activeQuery?.zips ?? []),
                ...(activeQuery?.counties ?? []),
                ...(activeQuery?.states ?? []),
            ]}
            startDate={
                activeQuery?.start_date
                    ? new Date(activeQuery.start_date)
                    : null
            }
            queryId={activeQuery?.id}
            pageId={pageId}
            endDate={
                activeQuery?.end_date ? new Date(activeQuery.end_date) : null
            }
            onReloadPress={onReloadPress}
            updatedDate={
                activeQuery?.date_last_used
                    ? new Date(activeQuery.date_last_used)
                    : undefined
            }
            onRunQueryPress={onRunQueryPress}
            onResetQueryPress={onResetQueryPress}
            onSaveQueryPress={onSaveQuery}
            onDownloadPress={() =>
                exportInsights(
                    InsightQueryType.PREP_CHECKS,
                    organization.slug,
                    activeQuery,
                    undefined,
                    [href.split('?')[0]]
                )
            }
            onNextQuestionPress={onNextQuestionPress}
            onPrevQuestionPress={onPrevQuestionPress}
            onBack={onBack}
            surveyData={surveyInsight}
            selectedLocationString={
                activeQuery?.zips?.join(', ') ||
                organization.jurisdiction_name ||
                'Full Jurisdiction'
            }
            selectedDatesString={buildQueryDateRange(activeQuery)}
            loading={loading || retrievingInsights}
        />
    ) : null
}
